import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import FluxiLogo from "../assets/svg/FluxiLogo.svg";
import { getMenuItems } from "../const/menuItems";
import { SidebarItem } from "./SidebarItem";
import { dictionary } from "../utils/dictionary";
import { useAuth } from "../context/AuthContext";
import { Chip } from "./common/chip/Chip";
import { ModalWrapper } from "./modals/ModalWrapper";
import { BuyCreditsModal } from "./modals/BuyCreditsModal";
import { membershipStatsAxios } from "../services/membership/MembershipAxios";
import { handleApi } from "../utils/handleApi";
import { useFinanceModuleFlag } from "../hooks/useFinanceModuleFlag";
import { useChatbotV2Flag } from "../hooks/useChatbotV2Flag";
import { useNavCollapse } from "../store/useNavCollapse";
import { decodeTokenService } from "../services/decodeTokenService";
import NotificationBell from "./notifications/NotificationBell";
import { isImmersiveRoute } from "../helpers/routeHelper";
import { navigateWithWorkspaceTransition } from "../utils/workspaceNavigation";
import { isProduction } from "../config/environment";

const Sidebar = ({ isTutorial }: { isTutorial: boolean }) => {
  // "collapsed" = modo workspace de Métricas (Lente y/o IA): el nav vive DEBAJO
  // de la barra blanca, angosto (104px) y sin bloque de logo (el wordmark ya
  // vive en esa barra). "hidden" = Lente + IA a la vez: desaparece del todo.
  const collapsed = useNavCollapse((state) => state.collapsed);
  const hidden = useNavCollapse((state) => state.hidden);
  const navigate = useNavigate();
  const location = useLocation();
  const [indicatorOffset] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [mascotCopyIndex, setMascotCopyIndex] = useState(() =>
    Math.floor(Math.random() * 20)
  );
  const [isOpenBuyCreditsModal, setIsOpenBuyCreditsModal] = useState(false);
  const [userCredits, setUserCredits] = useState(0);
  const [endMembershipDate, setEndMembershipDate] = useState("");
  const [loadingCredits, setLoadingCredits] = useState(false);
  const path = location.pathname;
  const { language, displayName } = useAuth();
  const { enabled: financeEnabled } = useFinanceModuleFlag();
  const { enabled: chatbotEnabled } = useChatbotV2Flag();
  const menuItems = getMenuItems({ financeEnabled, chatbotEnabled });
  const [planName, setPlanName] = useState("");
  const accountEmail = decodeTokenService.getEmailFromToken() || "";
  const accountInitials =
    (displayName || accountEmail)
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase())
      .join("") || "?";
  const mascotCopies = [
    "Psst... tu embudo me dijo que lo extrañas.",
    "No me mires así, yo no fui el que abandonó el checkout.",
    "Tranqui, tus clientes no se van a ir solos... ah, espera.",
    "Yo aquí trabajando y tú pasando el mouse por encima mío.",
    "Si cada vez que me tocas fuera una venta, ya serías millonario.",
    "Tu competencia acaba de publicar una página. Solo digo.",
    "Prometo no juzgarte si no has tocado tu embudo en 3 días.",
    "Tengo más energía que tu página de ventas un lunes.",
    "Dato curioso: los embudos también tienen sentimientos.",
    "Esta es tu señal para dejar de pasear y vender algo.",
    "Yo no duermo. Tu embudo tampoco. El único flojo aquí eres tú.",
    "Mi terapeuta dice que deje de cargar tus ventas. No le hice caso.",
    "Dicen que el dinero no da la felicidad. Pero un buen embudo sí.",
    "¿Dejaste el checkout sin botón de compra otra vez, verdad?",
    "Mientras lees esto, alguien cerró la pestaña de tu página.",
    "Soy un zorro de IA. Optimizar es literalmente lo mío.",
    "Tu embudo tiene más potencial que tu lista de tareas pendientes.",
    "No es por presionar, pero tus métricas me están mirando feo.",
    "¿El cliente que perdiste ayer volvió? Es broma, no volvió.",
    "Descansa tranquilo, yo cuido tus ventas. Bueno, lo intento.",
  ];

  // Precargar imagenes de la mascota para evitar parpadeo
  useEffect(() => {
    const imgs = [
      "https://fluxi.co/prod/assets/images/fluxi-sidebar-default.webp",
      "https://fluxi.co/prod/assets/images/fluxi-sidebar-hover.webp",
    ];
    imgs.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  // Rotar copy cada vez que se hace hover
  useEffect(() => {
    if (isHovered) {
      setMascotCopyIndex((prev) => (prev + 1) % mascotCopies.length);
    }
  }, [isHovered]);

  const handleBuyCredits = () => {
    setIsOpenBuyCreditsModal(true);
  };

  const handleMenuItemClick = (item: (typeof menuItems)[number]) => {
    if (isImmersiveRoute(item.path)) {
      navigateWithWorkspaceTransition(navigate, item.path, "enter");
      return;
    }
    navigate(item.path);
  };

  const closeModal = () => {
    setIsOpenBuyCreditsModal(false);
  };

  useEffect(() => {
    setLoadingCredits(true);
    const getStats = async () => {
      const { res } = await handleApi(membershipStatsAxios(), false);
      if (res) {
        if (!res.active_membership) {
          setLoadingCredits(false);
          return;
        }
        setUserCredits(res.active_membership.credits);
        setEndMembershipDate(res.active_membership.ends_at);
        setPlanName(res.active_membership.plan_name || "");
      }
      setLoadingCredits(false);
    };
    getStats();
  }, []);

  return (
    <>
      {isOpenBuyCreditsModal && (
        <ModalWrapper isOpen={isOpenBuyCreditsModal} onClose={closeModal}>
          <BuyCreditsModal
            onClose={closeModal}
            nextRenewalDate={endMembershipDate}
            language={language}
          />
        </ModalWrapper>
      )}
      <div
        className={`${
          // 104px en workspace = ancho de la zona del logo en la barra blanca
          // (LENS_RAIL_WIDTH de MetricsLensTopBar): columnas alineadas.
          hidden ? "w-0 overflow-hidden border-r-0" : collapsed ? "w-[104px]" : "w-60"
        } bg-[#FFFFFF] border-r border-gray-200 flex flex-col fixed left-0 transition-[width] duration-200`}
        style={
          collapsed
            ? { zIndex: 2, top: 64, height: "calc(100vh - 64px)" }
            : { zIndex: 2, top: 0, height: "100vh" }
        }
      >
        {/* Colapsado (workspace de Métricas): sin bloque de logo, solo el espaciador
            que trae develop. Expandido: logo a la izquierda y campana a la derecha. */}
        {collapsed ? (
          <div className="h-4 shrink-0" />
        ) : (
          <div className="flex h-[101px] items-center justify-between px-4">
            <div
              className="cursor-pointer"
              onClick={() => (isTutorial ? undefined : navigate("/home"))}
            >
              <img src={FluxiLogo} alt="Fluxi Logo" className="pl-3 h-auto" />
            </div>
            {!isTutorial && !isProduction && <NotificationBell />}
          </div>
        )}

        <nav className="sidebar-nav flex-1 overflow-y-auto">
          <div className="flex flex-col justify-between h-full">
            {/* Active indicator */}
            <div className="relative">
              <div
                className="absolute left-0 w-1 h-12 bg-[#EBEEFF] rounded-r-xl transition-all duration-200"
                style={{
                  transform: `translateY(${indicatorOffset}px)`,
                  opacity: indicatorOffset >= 0 ? 1 : 0,
                }}
              />

              {/* Main menu items */}
              <div className={`menu-section space-y-1 ${collapsed ? "px-2" : "px-4"}`}>
                {menuItems
                  .filter((item) => item.section === "main")
                  .map((item) => (
                    <SidebarItem
                      key={item.path}
                      icon={item.icon}
                      text={dictionary[item.text]?.[language] || item.text}
                      path={item.path}
                      active={location.pathname === item.path || (item.path === "/ads" && location.pathname.startsWith("/ads/"))}
                      disabled={
                        (location.pathname === "/plans" || isTutorial) ? true : item.disabled
                      }
                      onClick={() => handleMenuItemClick(item)}
                      collapsed={collapsed}
                    />
                  ))}
              </div>

              {/* Settings / cuenta: donde estaba Configuración va la fila de cuenta */}
              <div className={`menu-section space-y-1 ${collapsed ? "px-2" : "px-4"}`}>
                <div className={`border-t border-[#EAEAEA] mt-2 ${collapsed ? "mx-2" : "mx-4"}`}></div>
                {collapsed
                  ? menuItems
                      .filter((item) => item.section === "settings")
                      .map((item) => (
                        <SidebarItem
                          key={item.path}
                          icon={item.icon}
                          text={dictionary[item.text]?.[language] || item.text}
                          path={item.path}
                          active={location.pathname === item.path}
                          disabled={isTutorial ? true : item.disabled}
                          onClick={() => navigate(item.path)}
                          collapsed={collapsed}
                        />
                      ))
                  : (
                    <button
                      onClick={() => !isTutorial && navigate("/settings")}
                      className={`w-full flex items-center gap-3 mt-1 px-3 py-2.5 rounded-xl transition-colors text-left ${
                        location.pathname === "/settings" ? "bg-[#F4F7FE]" : "hover:bg-[#F4F7FE]"
                      }`}
                    >
                      <div className="w-9 h-9 rounded-full bg-[#2B3674] text-white flex items-center justify-center text-[13px] font-semibold flex-shrink-0">
                        {accountInitials}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[13px] font-semibold text-[#2B3674] truncate">
                          {displayName || accountEmail}
                        </p>
                        <p className="text-[11px] text-[#8A94AD] truncate" title={accountEmail}>
                          {planName || accountEmail}
                        </p>
                      </div>
                      <svg
                        className="w-[18px] h-[18px] flex-shrink-0"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#A3AED0"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <circle cx="12" cy="12" r="3" />
                        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                      </svg>
                    </button>
                  )}
              </div>

            </div>

            {!collapsed && (
            <div className="relative px-4 mt-auto overflow-visible">
              {/* Globo de texto */}
              <div
                className={`
                  absolute bottom-full left-1/2 -translate-x-1/2 mb-4 w-[75%]
                  transition-all duration-200 ease-in-out
                  ${isHovered
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-2 pointer-events-none"
                  }
                `}
              >
                <div
                  className="relative px-3 py-2.5 rounded-xl"
                  style={{
                    background: "white",
                    boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
                  }}
                >
                  <p className="text-sm leading-snug font-semibold text-gray-700 text-center">
                    {mascotCopies[mascotCopyIndex]}
                  </p>
                  {/* Puntica del globo */}
                  <div
                    className="absolute -bottom-[6px] left-1/2 -translate-x-1/2"
                    style={{
                      width: 0,
                      height: 0,
                      borderLeft: "7px solid transparent",
                      borderRight: "7px solid transparent",
                      borderTop: "7px solid white",
                      filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.04))",
                    }}
                  />
                </div>
              </div>

              {path !== "/home" && (
                <div
                  className="relative w-full"
                  onMouseEnter={() => setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
                >
                  <img
                    src="https://fluxi.co/prod/assets/images/fluxi-sidebar-default.webp"
                    alt="Fluxi Mascot"
                    className={`w-full transition-opacity duration-200 ease-in-out ${isHovered ? "opacity-0" : "opacity-100"}`}
                  />
                  <img
                    src="https://fluxi.co/prod/assets/images/fluxi-sidebar-hover.webp"
                    alt="Fluxi Mascot Excited"
                    className={`w-full absolute inset-0 transition-opacity duration-200 ease-in-out ${isHovered ? "opacity-100" : "opacity-0"}`}
                  />
                </div>
              )}
              {path == "/home" && (
                <div className="flex justify-center mb-6">
                  <Chip
                    text={dictionary["credits"][language]}
                    value={userCredits.toString()}
                    onClick={handleBuyCredits}
                    loading={loadingCredits}
                  />
                </div>
              )}
            </div>
            )}
          </div>
        </nav>
      </div>
    </>
  );
};

export default Sidebar;
